import { useEffect, useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { markdownToComponent } from '@/utils/convertMarkdown';
import formatName from '@/utils/format/formatName';
import getIcon from '@/utils/getIcon';
import { cn } from '@/utils/lib/utils';
import { AlertOctagon } from '@/utils/lucide';
import { ExternalLink } from '@/utils/lucide';
import { FileClock } from '@/utils/lucide';
import { Circle, Dot, RotateCw } from '@/utils/lucide';
//Icons
import { School } from '@/utils/lucide';
import { FileText } from '@/utils/lucide';
import openDetail from '@/utils/openDetail';
import { z_classes } from '@/utils/z';

import { ColumnType } from '@/types/DataGrid';
import { EvidenceExplanation } from '@/types/Evidence';

import { ConciseDocumentResultComponent } from '../Document/DocumentResult';
import OpenableTitle from '../Layout/OpenableTitle';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import ListCell from './ListCell';
import ResearchEvidencePanel from './ResearchEvidencePanel';
import TextCell from './TextCell';

const ResearchCell = ({ colType, cellProps }: { colType: ColumnType; cellProps?: any }) =>
{
	const value = cellProps.cell.getValue() as EvidenceExplanation;

	const [evidenceExplanation, setEvidenceExplanation] = useState<EvidenceExplanation | null>(null);

	const [popoverOpen, setPopoverOpen] = useState(false);
	const handleOpenChange = (open: boolean) =>
	{
		setPopoverOpen(open);

		if (open)
		{
			socket.emit('evidence_action', {
				action_type: 'get_evidence',
				related_type: 'custom_value',
				related_id: value.id
			});
		}
	};

	const allowRetry = cellProps?.table?.options?.allow_retry;
	const showRetry =
		value?.status === 'failed' || (allowRetry && value?.evidence_rating && value?.status !== 'actively_researching' && value?.status !== 'in_queue');

	const handleRetry = () =>
	{
		socket.emit('list_action', {
			action_type: 'retry_cell_research',
			custom_value_id: value.id
		});
	};

	let displayComponent;

	if (!value)
	{
		displayComponent = (
			<div>
				<p className='text-sm text-gray-400'>No Value Available</p>
			</div>
		);
	}
	else if (value.status !== undefined && value.status !== 'researched')
	{
		if (value.status === 'error' || value.status === 'cancelled' || value.status === 'failed')
		{
			displayComponent = (
				<div className='flex flex-row items-center text-maven-error-500 gap-x-2'>
					<p className='text-icon-sm'>
						<AlertOctagon />
					</p>
					<p className='text-sm '>{formatName(value.status)}</p>
					{showRetry && (
						<Button variant={'ghost'} className='text-gray-500' onClick={handleRetry}>
							<RotateCw className='text-icon-sm' />
							Retry
						</Button>
					)}
				</div>
			);
		}
		else if (value.status === 'in_progress' || value.status === 'actively_researching')
		{
			displayComponent = (
				<div className='flex flex-row items-center text-gray-500 gap-x-2'>
					<p className='text-icon-sm'>
						<RotateCw className='animate-spin' />
					</p>
					<p className='text-sm '>{formatName(value.status)}</p>
				</div>
			);
		}
		else
		{
			displayComponent = (
				<div className='flex flex-row items-center text-gray-500 gap-x-2'>
					<p className='text-icon-sm'>
						<FileClock />
					</p>
					<p className='text-sm '>{formatName(value.status)}</p>
				</div>
			);
		}
	}
	else if (value.value === undefined || value.value === null)
	{
		displayComponent = (
			<div>
				<p className='text-sm text-gray-400'>No Value Available</p>
			</div>
		);
	}
	else if (typeof value.value === 'string')
	{
		displayComponent = (
			<TextCell
				value={value}
				row={cellProps.row}
				column={cellProps.column}
				cell={cellProps.cell}
				md
				truncate={colType.truncate}
				table={cellProps.table}
			/>
		);
	}
	else if (typeof value.value === 'number' || typeof value.value === 'bigint' || typeof value.value === 'boolean')
	{
		displayComponent = (
			<div>
				<p>{`${value.value}`}</p>
			</div>
		);
	}
	else if (Array.isArray(value.value))
	{
		displayComponent = <ListCell value={value.value} row={cellProps.row} column={cellProps.column} cell={cellProps.cell} table={cellProps.table} />;
	}
	else
	{
		displayComponent = (
			<div>
				<p>{JSON.stringify(value.value ? value.value : value)}</p>
			</div>
		);
	}

	let evidenceRatingClass = 'text-gray-500';

	if (value?.evidence_rating === 'Direct Evidence')
	{
		evidenceRatingClass = 'text-maven-primary-500 fill-maven-primary-500';
	}
	else if (value?.evidence_rating === 'Indirect Evidence')
	{
		evidenceRatingClass = 'text-maven-indicator-500 fill-maven-indicator-500';
	}
	else
	{
		evidenceRatingClass = 'text-maven-error-500 fill-maven-error-500';
	}

	useEffect(() =>
	{
		socket.on('evidence_result', (data: any) =>
		{
			if (popoverOpen)
			{
				console.log('Retrieved evidence results', data);
				setEvidenceExplanation(data);
			}
		});

		return () =>
		{
			socket.off('evidence_result');
		};
	}, [popoverOpen]);

	if (!value || !value.evidence_rating || value.status === 'actively_researching')
	{
		return <div>{displayComponent}</div>;
	}

	return (
		<Popover open={popoverOpen} onOpenChange={handleOpenChange}>
			<PopoverTrigger>
				<Tooltip delayDuration={0}>
					<TooltipTrigger asChild>
						<div
							className={cn(
								'hover:underline text-start group cursor-pointer select-text',
								'hover:bg-gray-50 active:bg-gray-100',
								'transition-colors duration-100'
							)}
						>
							{!!value?.evidence_rating && (
								<p className={cn('text-[8px] absolute top-0.5 right-1', evidenceRatingClass)}>
									<Circle className='fill-current' />
								</p>
							)}

							{displayComponent}
						</div>
					</TooltipTrigger>
					<TooltipContent>
						<p>Click to view evidence</p>
					</TooltipContent>
				</Tooltip>
			</PopoverTrigger>
			<PopoverContent
				className={cn('flex flex-col w-[300px] md:w-[450px] xl:w-[600px] max-h-[70dvh] overflow-y-auto bg-white p-0', z_classes.overlay[1])}
				side='right'
				sideOffset={8}
				onOpenAutoFocus={e => e.preventDefault()}
			>
				{evidenceExplanation ? (
					<ResearchEvidencePanel evidence={evidenceExplanation} onRetry={showRetry ? handleRetry : undefined} />
				) : (
					<div className='flex flex-row items-center gap-2 p-2 text-gray-500'>
						<RotateCw className='text-icon animate-spin' />
						<p>Retrieving evidence explanation...</p>
					</div>
				)}
			</PopoverContent>
		</Popover>
	);
};

export default ResearchCell;
